//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import moment, { Moment } from 'moment-timezone';

const getComparator = (inclusive: boolean): (a: Moment, b: Moment) => boolean => inclusive
  ? (a, b) => a.isSameOrBefore(b)
  : (a, b) => a.isBefore(b);

function* genDatesInRange(start: Date, end: Date, opts: Required<IDateRangeOptions>) {
  const comparator = getComparator(opts.inclusive);
  const runningMoment = moment(start);
  const endMoment = moment(end);

  while (comparator(runningMoment, endMoment)) {
    yield runningMoment.toDate();
    runningMoment.add(1, 'd');
  }
}

function* genMomentDatesInRange(start: Date, end: Date, opts: Required<IDateRangeOptions>) {
  const comparator = getComparator(opts.inclusive);
  const runningMoment = moment(start);
  const endMoment = moment(end);

  while (comparator(runningMoment, endMoment)) {
    yield runningMoment.clone();
    runningMoment.add(1, 'd');
  }
}

interface IDateRangeOptions {
  /**
   * Inclusive on the final date? (e.g. if it is exactly midnight on a new day)
   * Defaults to true.
   */
  inclusive?: boolean;
}

const getDefaultedOpts = (opts?: IDateRangeOptions) => ({
  inclusive: true,
  ...opts,
});

/**
 * Get an array of all the dates in the range from start date to end date.
 * Time of day will be based on the start date time of day.
 */
export const getDatesInRange = (start: Date, end: Date, opts?: IDateRangeOptions) =>
  Array.from(genDatesInRange(start, end, getDefaultedOpts(opts)));

/**
 * Get an array of all the dates in the range from start date to end date, as Moment instances.
 * Time of day will be based on the start date time of day.
 */
export const getMomentDatesInRange = (start: Date, end: Date, opts?: IDateRangeOptions) =>
  Array.from(genMomentDatesInRange(start, end, getDefaultedOpts(opts)));
